import React from 'react';
import COLORS from '../theme/colors';

/**
 * @description Estado de carga reutilizable: spinner + texto centrado.
 * Nace del patrón que ya usaba Dashboard.js (fuente del rediseño) y reemplaza
 * los `<p>Cargando...</p>` desnudos que cada página de listado escribía a mano,
 * cada una con su propio texto y sin ningún estilo (hallazgo de auditoría
 * "Claude Design" #08).
 *
 * Dos tamaños, según dónde se use:
 * - Tamaño completo (default): las páginas de listado (Projects, WorkOrders,
 *   Equipment, Users, MaintenanceOrders, MaintenanceReports, Dashboard),
 *   ocupando el espacio de la tabla que todavía no cargó.
 * - `compact`: dentro de un Modal (los "Cargando detalle...", "Cargando datos
 *   del formulario...", "Cargando equipos..." de WorkOrders/MaintenanceOrders/
 *   MaintenanceReports) — mismo lenguaje visual pero con spinner y texto más
 *   chicos, para no imponer el tamaño "página completa" en un contenedor angosto.
 *
 * @param {string} [message='Cargando...'] Texto a mostrar debajo del spinner.
 * @param {boolean} [compact=false] Usa el tamaño reducido pensado para modales.
 * @returns {JSX.Element}
 */
const LoadingState = ({ message = 'Cargando...', compact = false }) => (
  <div style={compact ? styles.centeredCompact : styles.centered}>
    <div style={compact ? styles.spinnerCompact : styles.spinner} />
    <p style={compact ? styles.loadingTextCompact : styles.loadingText}>{message}</p>
  </div>
);

const styles = {
  centered: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '60px 0',
    gap: '12px',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: `3px solid rgba(26,184,171,0.2)`,
    borderTop: `3px solid ${COLORS.teal}`,
    borderRadius: '50%',
    // @keyframes spin definido globalmente en src/index.css
    animation: 'spin 0.8s linear infinite',
  },
  loadingText: {
    color: 'rgba(56,62,65,0.5)',
    fontSize: '14px',
  },
  // Variante compacta: mismo lenguaje visual, pensada para el espacio angosto
  // de un Modal en vez del área completa de una página de listado.
  centeredCompact: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px 0',
    gap: '8px',
  },
  spinnerCompact: {
    width: '22px',
    height: '22px',
    border: `2.5px solid rgba(26,184,171,0.2)`,
    borderTop: `2.5px solid ${COLORS.teal}`,
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  loadingTextCompact: {
    color: 'rgba(56,62,65,0.5)',
    fontSize: '13px',
  },
};

export default LoadingState;
